import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../store/auth/actions';
import { RootState } from '../store/reducers';
import { Eye, EyeOff } from 'lucide-react';
import logoTca from '../logo-tca.png';
import './Auth.css';

const Auth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, errors } = useSelector((state: RootState) => state.auth);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginRequest({ email, password }, navigate));
  };

  return (
    <div className="auth-page">
      {/* Left panel - TCA institutional branding */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <p className="auth-republic">République de Côte d'Ivoire</p>

          <div className="auth-logo">
            <img src={logoTca} alt="Tribunal de Commerce d'Abidjan" className="auth-logo-img" />
          </div>

          <p className="auth-institution-name">Tribunal de Commerce d'Abidjan</p>

          <h2 className="auth-left-title">
            Plateforme d'Anonymisation des Décisions de Justice
          </h2>
          <p className="auth-left-description">
            Système sécurisé de traitement et d'anonymisation des décisions
            rendues par le Tribunal de Commerce d'Abidjan.
            Conforme aux exigences de protection des données personnelles.
          </p>

          <div className="auth-left-features">
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <strong>Anonymisation conforme</strong>
                <p>Protection des données personnelles selon les normes en vigueur</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="3" x2="12" y2="21" />
                  <path d="M17 8l-5-5-5 5" />
                  <path d="M17 16l-5 5-5-5" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                </svg>
              </div>
              <div>
                <strong>Décisions commerciales</strong>
                <p>Traitement spécialisé des litiges du tribunal de commerce</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div>
                <strong>Archivage sécurisé</strong>
                <p>Conservation et publication encadrées des décisions rendues</p>
              </div>
            </div>
          </div>

          <div className="auth-left-separator">
            <span className="auth-separator-diamond" />
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="auth-right-panel">
        <div className="auth-form-wrapper">
          <div className="auth-form-logo-mobile">
            <img src={logoTca} alt="TCA" />
          </div>

          <div className="auth-form-header">
            <h1 className="auth-form-title">Se connecter</h1>
            <p className="auth-form-subtitle">
              Accédez à votre espace de travail
            </p>
          </div>

          {errors && (
            <div className="auth-error-message">
              {typeof errors === 'string' ? errors : 'Une erreur est survenue'}
            </div>
          )}

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Mot de passe</label>
              <div className="input-password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </form>

          <p className="auth-admin-note">
            Accès réservé au personnel autorisé du Tribunal de Commerce d'Abidjan
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
